/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { Archive, ArchiveDTO } from 'src/app/_model/archive';
import { ArchivesDaoService } from '../../dao/archives-dao.service';
import { ArchivesService } from '../archives.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads App Parameters
 */
@Injectable()
export class ArchivesLogicService implements ArchivesService {
  public archives$ = new BehaviorSubject<Archive[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private archiveDaoService: ArchivesDaoService,
    private authenticationService: AuthenticationService
  ) {
    this.initialize();
  }

  // Resetable
  public async reset() {
    await this.archiveDaoService.reset();
  }

  public async clear() {
    await this.archiveDaoService.clear();
    this.archives$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteArchives(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  
  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING)
    try {
      await this.loadLocalArchives();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED)
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE)
    }
  }

  // Private Functions
  private async loadLocalArchives() {
    try {
      this.logger.log(this.constructor.name, '- Load local Archives');
      let archives: Archive[] = await this.archiveDaoService.getArchives();

      this.logger.log(this.constructor.name, '- Load local Archives', archives);

      this.archives$.next(archives);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Archives: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemoteArchives(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load remote Archives from IMS');

      try {
        let archives: Archive[] = [];
        let archivesDto: ArchiveDTO[] = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'syncarchives',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey
            }
          })
        );

        this.logger.log(
          this.constructor.name,
          'Downloaded ArchivesDto: ',
          archivesDto
        );

        for (const archiveDto of archivesDto) {
          archives.push(Archive.fromDto(archiveDto));
        }

        this.logger.log(
          this.constructor.name,
          'Downloaded Archives: ',
          archives
        );

        await this.archiveDaoService.saveArchives(archives);
        await this.archives$.next(archives);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Archives: ',
          error?.message
        );
        throw error;
      }
    } else {
      let error = new Error(
        'Cannot download Worktypes - user not authenticated'
      );
      this.logger.error(this.constructor.name, error?.message);
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {

  }

}
